import { Device } from "api/types/device";
import { DeviceConfiguration } from "models/devices/configuration";
import DeviceCard from "widgets/cards/device";
import LabeledField from "providers/labeledField";
import { useTranslation } from "react-i18next";
import { formatDate } from "util/datelib";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import GatewayUptimeChart from "./gateway-chart";

interface GatewayProps {
  device: Device;
  config: DeviceConfiguration;
}

const GatewayOverview: React.FC<GatewayProps> = ({ device, config }) => {
  const { t } = useTranslation();
  const sites = useSelector(accountSiteList, shallowEqual);

  const siteName =
    (device?.siteId && sites.find((s) => s.id === device.siteId)?.name) ||
    (device?.classes?.[0] &&
      sites.find((s) => s.id === device.classes[0])?.name) ||
    t("label.none");

  const lastCommsRaw = device?.lastComms
    ? formatDate(device.lastComms, "lastcomms")
    : "";
  const lastComms =
    lastCommsRaw.trim().length > 0 ? lastCommsRaw : t("label.none");

  const createdRaw = device?.created ? formatDate(device.created, "default") : "";
  const created = createdRaw.trim().length > 0 ? createdRaw : t("label.none");

  const signalText =
    device?.signal != null && !Number.isNaN(device.signal)
      ? `${device.signal} dBm`
      : t("label.none");

  const batteryText =
    device?.battery != null && !Number.isNaN(device.battery)
      ? `${device.battery.toFixed(2)} V`
      : t("label.none");

  const childCount = device?.children?.length ?? 0;

  const chartHeight = 320;

  return (
    <div className="flex w-full flex-col gap-4">
      <DeviceCard title={t("device.gateway.overview")} config={config}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.deployed")}
            id="gateway-deployed"
          >
            <div className="text-txt-lg text-white">
              {device?.deployed ? t("common.yes") : t("common.no")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("global.result.item_lastcomms")}
            id="gateway-last-comms"
          >
            <div className="text-txt-lg text-white">{lastComms}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.created")}
            id="gateway-created"
          >
            <div className="text-txt-lg text-white">{created}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("label.classes")}
            id="gateway-site"
          >
            <div className="text-txt-lg text-white">{siteName}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("label.host")}
            id="gateway-host"
          >
            <div className="text-txt-lg text-white">
              {device?.host?.trim() ? device.host : t("label.none")}
            </div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.signal")}
            id="gateway-signal"
          >
            <div className="text-txt-lg text-white">{signalText}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.battery")}
            id="gateway-battery"
          >
            <div className="text-txt-lg text-white">{batteryText}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.children")}
            id="gateway-children"
          >
            <div className="text-txt-lg text-white">{childCount}</div>
          </LabeledField>
          <LabeledField
            className="text-txt-xs"
            textColor="white"
            label={t("device.gateway.dataQuality")}
            id="gateway-data-quality"
          >
            <div className="text-txt-lg text-white">
              {device?.dataQuality?.trim()
                ? device.dataQuality
                : t("label.none")}
            </div>
          </LabeledField>
        </div>
      </DeviceCard>
      <div className="w-full">
        <GatewayUptimeChart
          device={device}
          config={config}
          height={chartHeight}
        />
      </div>
    </div>
  );
};

export default GatewayOverview;
